import React, { useEffect, useMemo, useState } from 'react';
import { Button, Spinner, Label } from 'reactstrap';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { api } from '../../../../../../../config';
import { getAuthToken } from '../../../../../../../auth/tokenvalidation';
import { formatDateToddmmyyhhmm } from '../../../../../../../utils/dateformatter';
import useSubmittedBidder from '../../../../../hooks/useSubmittedBidder';
import useClarificationToleranceValidityResponses from '../../../../../../../shared/offer-domain/respond/useClarificationToleranceValidityResponses';
import {
  getOwnClauseResponse,
  hasClauseResponse,
  isEvaluatorResponse,
  needsIcReplyOnEvaluatorThread,
  getLastOwnEditableResponse,
  isLastResponseEvaluator,
  needsTeAgreeOnLastCounter,
  hasTeClarificationRespondReady,
  canAgreeLastCounter,
  toClauseResponseList,
  sortRespondClauseDetails,
  toLockedFlag,
  TYPE_RESPONSE_DOCUMENT,
  createLockRespondHandler,
} from '../../../../../../../shared/offer-domain/respond';
import {
  SectionHeader,
  SectionCard,
  RespondNotesEditor,
  RespondMessageBubble,
} from '../../../../../../../shared/offer-domain/ui';
import { AgreeLockButton } from '../../../../../../../shared/offer-domain/clause';
import IcRespondAnchor from '../components/IcRespondAnchor';
import { unwrapApiItems } from '../../../../../../../shared/api/unwrap';
import {
  mapSupportDocumentItem,
  mapCounterDocumentItem,
  isOtherSupportDocument,
  mergeDocumentsWithCounters,
  DocumentRow,
} from '../../../../../../../shared/offer-domain/document';

const DocumentResponseSection = ({
  procurementId,
  offerId,
  hasDocumentCounter = false,
  procurementMitraId: procurementMitraIdProp = null,
  isEvaluatorView = true,
  isRespondClosed: isRespondClosedProp = null,
  highlightArmed = false,
  onPendingRespondIds,
}) => {
  const token = getAuthToken();
  const { items } = useSubmittedBidder(procurementId);
  const bidder = useMemo(() => {
    const matched =
      items.find(
        item =>
          String(item.idOffer) === String(offerId) ||
          String(item.id) === String(offerId) ||
          (procurementMitraIdProp &&
            String(item.id) === String(procurementMitraIdProp))
      ) ?? null;
    if (procurementMitraIdProp) {
      return {
        ...matched,
        id: procurementMitraIdProp,
        idOffer: offerId ?? matched?.idOffer,
      };
    }
    return matched;
  }, [items, offerId, procurementMitraIdProp]);

  const isRespondClosed = Boolean(isRespondClosedProp);

  const {
    responses,
    isLoading: isResponsesLoading,
    isSaving,
    saveResponse,
    deleteResponse,
    lockResponse,
  } = useClarificationToleranceValidityResponses(
    token,
    procurementId,
    bidder?.id,
    isEvaluatorView
  );

  const handleLock = createLockRespondHandler(lockResponse);

  const responseList = useMemo(() => {
    const documentResponses = responses[TYPE_RESPONSE_DOCUMENT] ?? [];
    return sortRespondClauseDetails(
      toClauseResponseList(documentResponses).filter(item =>
        hasClauseResponse(item?.respondNotes)
      )
    );
  }, [responses]);
  const ownResponse = useMemo(
    () =>
      getOwnClauseResponse(responseList, {
        isEvaluator: isEvaluatorView,
      }),
    [isEvaluatorView, responseList]
  );
  const lastOwnEditable = useMemo(
    () =>
      getLastOwnEditableResponse(responseList, {
        isEvaluator: isEvaluatorView,
      }),
    [isEvaluatorView, responseList]
  );
  const savedNotes = isEvaluatorView
    ? (ownResponse?.respondNotes ?? '')
    : (lastOwnEditable?.respondNotes ?? '');
  const hasOwnSaved = hasClauseResponse(savedNotes);
  const messageCount = responseList.length;
  const hasAnySaved = messageCount > 0;

  const [isExpanded, setIsExpanded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draftNotes, setDraftNotes] = useState('');

  useEffect(() => {
    setIsExpanded(false);
    setIsEditing(false);
    setDraftNotes('');
  }, [offerId, bidder?.id]);

  useEffect(() => {
    if (!isEditing) setDraftNotes(savedNotes);
  }, [savedNotes, isEditing]);

  useEffect(() => {
    if (hasAnySaved || hasDocumentCounter) setIsExpanded(true);
  }, [hasAnySaved, hasDocumentCounter]);

  const canReply = isRespondClosed
    ? false
    : isEvaluatorView
      ? !isLastResponseEvaluator(responseList)
      : needsIcReplyOnEvaluatorThread(responseList);
  const isThreadLocked = toLockedFlag(
    responseList[responseList.length - 1]?.isLocked
  );
  const needsAgree = needsTeAgreeOnLastCounter(responseList);
  const showRespondButton = canReply && !isEditing && !isThreadLocked;

  useEffect(() => {
    if (highlightArmed && (isEvaluatorView ? needsAgree : canReply)) {
      setIsExpanded(true);
    }
  }, [highlightArmed, canReply, isEvaluatorView, needsAgree]);

  useEffect(() => {
    if (!onPendingRespondIds || isResponsesLoading) return;
    const pending = isEvaluatorView
      ? needsAgree
        ? [{ id: 'document', tab: 'document-attachment' }]
        : []
      : canReply
        ? [{ id: 'document', tab: 'document-attachment' }]
        : [];
    onPendingRespondIds(pending, {
      hasClarificationRespond: isEvaluatorView
        ? hasTeClarificationRespondReady(responseList)
        : true,
    });
  }, [
    canReply,
    isEvaluatorView,
    needsAgree,
    onPendingRespondIds,
    isResponsesLoading,
    responseList,
  ]);

  const handleStartRespond = () => {
    setDraftNotes(lastOwnEditable ? savedNotes : '');
    setIsEditing(true);
    setIsExpanded(true);
  };

  const handleCancel = () => {
    setDraftNotes(savedNotes);
    setIsEditing(false);
    if (!hasOwnSaved) setIsExpanded(false);
  };

  const handleSubmit = async () => {
    const notes = String(draftNotes ?? '').trim();
    if (!hasClauseResponse(notes)) {
      toast.error('Respond notes cannot be empty');
      return;
    }

    try {
      const saved = await saveResponse(TYPE_RESPONSE_DOCUMENT, notes);
      if (!saved) {
        toast.error('Failed to save response');
        return;
      }
      setIsEditing(false);
      toast.success('Response saved successfully');
    } catch {
      toast.error('Failed to save response');
    }
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This response note will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f1556c',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, Delete!',
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      await deleteResponse(TYPE_RESPONSE_DOCUMENT);
      setDraftNotes('');
      setIsEditing(false);
      const remaining = responseList.filter(
        item => item?.id !== ownResponse?.id
      );
      setIsExpanded(
        remaining.some(item => hasClauseResponse(item?.respondNotes))
      );
      toast.success('Response removed successfully');
    } catch {
      toast.error('Failed to remove response');
    }
  };

  const isDraftDirty = String(draftNotes ?? '') !== String(savedNotes ?? '');

  return (
    <IcRespondAnchor
      id='document'
      pendingIds={
        highlightArmed && (isEvaluatorView ? needsAgree : canReply)
          ? ['document']
          : []
      }
    >
      <SectionCard>
        <SectionHeader
          title='Document Response'
          subtitle='Clarification notes related to supporting documents'
        />
        <div className='border rounded-3 bg-white p-4'>
          <div className='d-flex justify-content-between align-items-center'>
            <div
              className='d-flex align-items-center gap-2'
              style={{ cursor: 'pointer' }}
              onClick={() => setIsExpanded(prev => !prev)}
            >
              <span
                className={`rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 ${
                  isExpanded
                    ? 'bg-primary-subtle text-primary'
                    : 'bg-light text-muted'
                }`}
                style={{ width: '28px', height: '28px' }}
              >
                <i
                  className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line fs-16`}
                ></i>
              </span>
              {hasAnySaved || hasDocumentCounter ? (
                <>
                  <span
                    className='bg-danger text-white rounded-pill d-inline-flex align-items-center justify-content-center fw-semibold fs-11'
                    style={{
                      minWidth: '20px',
                      height: '20px',
                      padding: messageCount > 9 ? '0 6px' : 0,
                    }}
                  >
                    {messageCount}
                  </span>
                  <span className='fw-semibold text-dark fs-13'>Response</span>
                </>
              ) : (
                <span className='text-muted fs-13'>No responses yet</span>
              )}
            </div>

            {showRespondButton && (
              <Button
                color='light'
                size='sm'
                className='border bg-white text-muted px-3 py-1 d-flex align-items-center gap-1 fs-13 rounded-2 shadow-sm'
                onClick={handleStartRespond}
                disabled={isSaving}
              >
                <span>Respond</span>
                <i className='ri-chat-3-line fs-14 align-middle ms-1'></i>
              </Button>
            )}
          </div>

          {isExpanded && responseList.length > 0 && (
            <div className='mt-3'>
              {responseList.map((response, index) => {
                const evaluatorItem = isEvaluatorResponse(response);
                const metaDate = formatDateToddmmyyhhmm(
                  response?.updatedDate || response?.createdDate
                );
                const authorLabel = evaluatorItem
                  ? response.createdByLabel || 'PT Pertamina Patra Niaga'
                  : response.createdByLabel || 'Counter';
                const isOwnEditing =
                  isEditing &&
                  lastOwnEditable &&
                  String(ownResponse?.id ?? lastOwnEditable?.id ?? '') ===
                    String(response?.id ?? '');

                if (isOwnEditing) return null;

                return (
                  <RespondMessageBubble
                    key={response.id ?? `${index}-${response.respondNotes}`}
                    isEvaluator={evaluatorItem}
                    content={response?.respondNotes}
                    authorLabel={authorLabel}
                    metaDate={metaDate}
                    footer={
                      <>
                        {canAgreeLastCounter(responseList, response) &&
                          (!isRespondClosed || response.isLocked) && (
                            <div className='mt-3 pt-2 border-top'>
                              <AgreeLockButton
                                embedded
                                isLocked={response.isLocked}
                                onLock={
                                  !isRespondClosed &&
                                  isEvaluatorView &&
                                  response.id
                                    ? locked => handleLock(response.id, locked)
                                    : undefined
                                }
                                disabled={isSaving}
                              />
                            </div>
                          )}
                        {lastOwnEditable &&
                          String(response.id) === String(lastOwnEditable.id) &&
                          !response.isLocked &&
                          !isRespondClosed && (
                            <div className='mt-3 pt-2 border-top d-flex align-items-center gap-2'>
                              <Button
                                color='light'
                                size='sm'
                                className='border bg-white text-primary fw-medium fs-13 flex-grow-1'
                                onClick={() => setIsEditing(true)}
                                disabled={isSaving}
                              >
                                Edit
                              </Button>
                              <Button
                                color='light'
                                size='sm'
                                className='border bg-white text-danger fw-medium fs-13 flex-grow-1'
                                onClick={handleDelete}
                                disabled={isSaving}
                              >
                                Remove
                              </Button>
                            </div>
                          )}
                      </>
                    }
                  />
                );
              })}
            </div>
          )}

          {isExpanded &&
            isEditing &&
            !isThreadLocked &&
            !isRespondClosed &&
            (isEvaluatorView ? true : canReply || Boolean(lastOwnEditable)) && (
              <div className='mt-3'>
                <div className='d-flex justify-content-between align-items-center mb-2'>
                  <Label className='form-label fw-semibold text-dark fs-13 mb-0'>
                    Respond Notes
                  </Label>
                  {isDraftDirty && (
                    <span className='text-warning small fs-12 fw-medium d-flex align-items-center gap-1'>
                      <i className='ri-error-warning-line fs-14'></i>
                      Unsaved Changes
                    </span>
                  )}
                </div>

                <RespondNotesEditor
                  id='respond-document'
                  value={draftNotes}
                  onChange={e => setDraftNotes(e.target.value)}
                  isDraftDirty={isDraftDirty}
                />

                <div className='d-flex gap-2 mt-3 justify-content-end'>
                  <Button
                    color='light'
                    className='border bg-white text-dark fw-semibold px-4 rounded-3'
                    onClick={handleCancel}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                  <Button
                    color='primary'
                    className='fw-semibold px-4 rounded-3'
                    onClick={handleSubmit}
                    disabled={isSaving || !isDraftDirty}
                  >
                    {isSaving ? 'Saving...' : 'Send Response'}
                  </Button>
                </div>
              </div>
            )}
        </div>
      </SectionCard>
    </IcRespondAnchor>
  );
};

const DocumentAttachmentTab = ({
  procurementId,
  offerId,
  procurementMitraId = null,
  isEvaluatorView = true,
  isRespondClosed = null,
  highlightArmed = false,
  onPendingRespondIds,
}) => {
  const token = getAuthToken();
  const [procurementDocuments, setProcurementDocuments] = useState([]);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(procurementId && token));

  useEffect(() => {
    if (!procurementId || !token) return;

    let cancelled = false;

    const fetchDocuments = async () => {
      setIsLoading(true);
      try {
        const [docResponse, counterResponse] = await Promise.all([
          fetch(
            `${api.API_URL}/procurement/get-procurement-document?ProcurementId=${encodeURIComponent(
              procurementId
            )}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          ),
          fetch(
            `${api.API_URL}/procurement-document-counter/by-procurement/${encodeURIComponent(
              procurementId
            )}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          ),
        ]);

        if (!docResponse.ok) {
          throw new Error(`HTTP error! status: ${docResponse.status}`);
        }

        const docResult = await docResponse.json();
        let counters = [];
        if (counterResponse.ok) {
          const counterResult = await counterResponse.json();
          counters = unwrapApiItems(counterResult).map(mapCounterDocumentItem);
        }

        const items = unwrapApiItems(docResult)
          .map(mapSupportDocumentItem)
          .filter(item => item.isShowToDMUT === true || item.isShowToDMUT === 1)
          .map(item => ({
            ...item,
            updated: item.lastUpdated || item.createdDate,
          }));

        const merged = mergeDocumentsWithCounters(items, counters);

        if (!cancelled) {
          setProcurementDocuments(
            merged.filter(item => !isOtherSupportDocument(item))
          );
          setOtherDocuments(
            merged.filter(item => isOtherSupportDocument(item))
          );
        }
      } catch {
        if (!cancelled) {
          setProcurementDocuments([]);
          setOtherDocuments([]);
          toast.error('Failed to load supporting documents');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [procurementId, token]);

  const handleView = url => {
    if (!url) {
      toast.error('File is not available');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getBadgeMeta = doc => {
    if (doc.hasCounter) {
      return {
        label: 'Countered',
        className: 'bg-warning-subtle text-warning',
      };
    }
    if (doc.isMasterDocument) {
      return {
        label: 'Master Document',
        className: 'bg-success-subtle text-success',
      };
    }
    return {
      label: 'Upload Custom',
      className: 'bg-primary-subtle text-primary',
    };
  };

  const hasDocumentCounter = useMemo(
    () =>
      [...procurementDocuments, ...otherDocuments].some(doc => doc.hasCounter),
    [procurementDocuments, otherDocuments]
  );

  return (
    <>
      <SectionCard>
        <SectionHeader
          title='Procurement Documents'
          subtitle='Review supporting documents submitted for this offering.'
        />

        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner size='sm' color='primary' />
            <span className='text-muted ms-2 fs-13'>Loading documents...</span>
          </div>
        ) : procurementDocuments.length === 0 ? (
          <div
            className='border rounded-3 p-4 d-flex align-items-center justify-content-center text-muted bg-light bg-opacity-50'
            style={{ minHeight: '120px' }}
          >
            <i className='ri-file-list-3-line fs-24 me-2' />
            No procurement documents available.
          </div>
        ) : (
          <div>
            {procurementDocuments.map(doc => {
              const badge = getBadgeMeta(doc);
              return (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  badgeLabel={badge.label}
                  badgeClass={badge.className}
                  onView={handleView}
                />
              );
            })}
          </div>
        )}
      </SectionCard>

      <SectionCard>
        <SectionHeader title='Other Documents' />

        {isLoading ? (
          <div className='text-center py-4'>
            <Spinner size='sm' color='primary' />
          </div>
        ) : otherDocuments.length === 0 ? (
          <div
            className='border rounded-3 p-4 d-flex align-items-center justify-content-center text-muted bg-light bg-opacity-50'
            style={{ minHeight: '120px' }}
          >
            <i className='ri-upload-cloud-2-line fs-24 me-2' />
            No other documents available.
          </div>
        ) : (
          <div>
            {otherDocuments.map(doc => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                badgeLabel='Upload Custom'
                badgeClass='bg-primary-subtle text-primary'
                onView={handleView}
              />
            ))}
          </div>
        )}
      </SectionCard>

      <DocumentResponseSection
        procurementId={procurementId}
        offerId={offerId}
        hasDocumentCounter={hasDocumentCounter}
        procurementMitraId={procurementMitraId}
        isEvaluatorView={isEvaluatorView}
        isRespondClosed={isRespondClosed}
        highlightArmed={highlightArmed}
        onPendingRespondIds={onPendingRespondIds}
      />
    </>
  );
};

export default DocumentAttachmentTab;
